/**
 * Register API execution history — pure presentation (AVA-602, showcase D / plan N29).
 * Input: ApiExecutionVM (startedAt, completedAt, status, triggerType, recordsFetched / Imported /
 * Updated / Failed / Skipped, facilitySkippedRows, facilityGroupLevelRows, errorDetails,
 * warnings[{ rowIndex, validationErrors, wasSkipped, reasonCode? }], newRows?).
 */
import { FREQUENCY, frequencyLabel, isScheduled } from '../../../modules/apiSourceRules.js';

export const EXECUTION_STATUS = Object.freeze({ RUNNING: 0, SUCCESS: 1, PARTIAL: 2, FAILED: 3 });
export const TRIGGER = Object.freeze({ MANUAL: 0, SCHEDULED: 1 });

/** Rows a reason group lists before "Show all". */
export const GROUP_FIRST_N = 5;
export const OTHER_REASON = 'other';

const STATUS = Object.freeze({
  [EXECUTION_STATUS.RUNNING]: { tone: 'running', icon: 'mdi-loading', label: 'Running' },
  [EXECUTION_STATUS.SUCCESS]: { tone: 'ok', icon: 'mdi-check-circle', label: 'Success' },
  [EXECUTION_STATUS.PARTIAL]: { tone: 'partial', icon: 'mdi-alert', label: 'Partial' },
  [EXECUTION_STATUS.FAILED]: { tone: 'failed', icon: 'mdi-close-circle', label: 'Failed' },
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad = (n) => String(n).padStart(2, '0');
const toNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

export const fmtCount = (value) => toNumber(value).toLocaleString('en-US');

export function statusPresentation(status) {
  return STATUS[status] || { tone: 'unknown', icon: 'mdi-help-circle-outline', label: 'Unknown' };
}

export function triggerPresentation(triggerType) {
  return triggerType === TRIGGER.SCHEDULED
    ? { icon: 'mdi-clock-outline', label: 'Scheduled' }
    : { icon: 'mdi-hand-back-right', label: 'Manual' };
}

function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "Today" / "Yesterday" / "Tomorrow" / "22 Sep" (local time; the year only when it differs from `now`'s). */
export function dayLabel(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return '—';
  const today = toDate(now) || new Date();
  if (sameDay(date, today)) return 'Today';
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (sameDay(date, yesterday)) return 'Yesterday';
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (sameDay(date, tomorrow)) return 'Tomorrow';
  const base = `${date.getDate()} ${MONTHS[date.getMonth()]}`;
  return date.getFullYear() === today.getFullYear() ? base : `${base} ${date.getFullYear()}`;
}

/** 24-hour local time, "06:00". */
export function timeLabel(value) {
  const date = toDate(value);
  return date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : '—';
}

/** "412ms" / "38.0s" / "2m 11s"; null while the run has no completion time. */
export function durationLabel(run) {
  const start = toDate(run && run.startedAt);
  const end = toDate(run && run.completedAt);
  if (!start || !end) return null;
  const ms = Math.max(0, end - start);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

/** New rows of a run: the API's `newRows` when it sends one, else imported − updated. */
export function addedCount(run) {
  if (run && run.newRows !== undefined && run.newRows !== null && Number.isFinite(Number(run.newRows))) {
    return Number(run.newRows);
  }
  return Math.max(0, toNumber(run && run.recordsImported) - toNumber(run && run.recordsUpdated));
}

/**
 * The numeric cells of a run row. `null` = not applicable (rendered as —): nothing is counted while
 * a run is in flight, and a failed run imported nothing.
 */
export function runCounts(run) {
  const status = run ? run.status : undefined;
  const done = status !== EXECUTION_STATUS.RUNNING;
  const counted = done && status !== EXECUTION_STATUS.FAILED;
  const fetched = toNumber(run && run.recordsFetched);
  return {
    fetched: done && fetched > 0 ? fetched : null,
    added: counted ? addedCount(run) : null,
    updated: counted ? toNumber(run.recordsUpdated) : null,
    skipped: counted ? toNumber(run.recordsSkipped) : null,
  };
}

/** A run has details to expand once it finished with warnings or a message. */
export function isExpandable(run) {
  if (!run || run.status === EXECUTION_STATUS.RUNNING) return false;
  return (Array.isArray(run.warnings) && run.warnings.length > 0) || !!(run.errorDetails && String(run.errorDetails).trim());
}

/** "MANDATORY_FIELD_EMPTY" / "mandatoryFieldEmpty" → "Mandatory field empty". */
export function humanizeReasonCode(code) {
  const words = String(code || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Other';
}

/**
 * Row warnings grouped by the API's `reasonCode`. Warnings without one fall into a neutral
 * "Other" group — the message text is shown, never parsed for a reason.
 * @returns {Array<{key, label, icon, count, skipped, kept, items: Array<{row, message}>}>} largest first
 */
export function groupWarnings(warnings) {
  const groups = new Map();
  (Array.isArray(warnings) ? warnings : []).forEach((warning) => {
    if (!warning) return;
    const code = typeof warning.reasonCode === 'string' && warning.reasonCode.trim() ? warning.reasonCode.trim() : '';
    const key = code || OTHER_REASON;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label: code ? humanizeReasonCode(code) : 'Other',
        icon: code ? 'mdi-alert-circle-outline' : 'mdi-format-list-bulleted',
        count: 0,
        skipped: 0,
        kept: 0,
        items: [],
      };
      groups.set(key, group);
    }
    const messages = (warning.validationErrors || warning.errors || []).filter((m) => m !== null && m !== undefined && String(m).trim());
    const lines = messages.length ? messages : [''];
    lines.forEach((message) => {
      group.count += 1;
      if (warning.wasSkipped) group.skipped += 1;
      else group.kept += 1;
      group.items.push({ row: warning.rowIndex, message: String(message) });
    });
  });
  return [...groups.values()].sort((a, b) => {
    if (a.key === OTHER_REASON && b.key !== OTHER_REASON) return 1;
    if (b.key === OTHER_REASON && a.key !== OTHER_REASON) return -1;
    return b.count - a.count;
  });
}

/** Tag next to a reason group: every row skipped, every row kept, or mixed (none). */
export function groupOutcome(group) {
  if (group.skipped && !group.kept) return { tone: 'warn', text: 'Skipped' };
  if (group.kept && !group.skipped) return { tone: 'ok', text: 'Imported with warning' };
  return null;
}

/** The facts line of an expanded run. */
export function runFacts(run) {
  const facts = [];
  if (run.status === EXECUTION_STATUS.FAILED && !(run.warnings || []).length) {
    if (toNumber(run.recordsFetched)) {
      facts.push({ label: 'Fetched', value: fmtCount(run.recordsFetched) });
      facts.push({ label: 'Imported', value: '0' });
    }
    return facts;
  }
  facts.push({ label: 'Imported (added + updated)', value: fmtCount(run.recordsImported) });
  facts.push({ label: 'Failed', value: fmtCount(run.recordsFailed) });
  if (toNumber(run.recordsSkipped)) facts.push({ label: 'Skipped', value: fmtCount(run.recordsSkipped) });
  if (toNumber(run.facilitySkippedRows)) facts.push({ label: 'of which Facility assignment', value: fmtCount(run.facilitySkippedRows) });
  const groupLevel = toNumber(run.facilityGroupLevelRows);
  if (groupLevel) {
    facts.push({
      label: groupLevel === toNumber(run.recordsImported) ? 'All rows at Facility Group level (Shared)' : 'At Facility Group level',
      value: fmtCount(groupLevel),
    });
  }
  if (run.completedAt) facts.push({ label: 'Completed', value: timeLabel(run.completedAt) });
  return facts;
}

/** A failed run without row warnings shows its message in a box instead of reason groups. */
export function failureMessage(run) {
  if (!run || run.status !== EXECUTION_STATUS.FAILED || (run.warnings || []).length) return '';
  return (run.errorDetails && String(run.errorDetails).trim()) || 'The run failed without a message.';
}

/** "Daily at 06:00 UTC" etc. from ScheduleConfigDto. */
export function scheduleLabel(schedule) {
  const s = schedule || {};
  const frequency = Number(s.frequency || 0);
  const time = `${pad(toNumber(s.hour))}:${pad(toNumber(s.minute))} UTC`;
  switch (frequency) {
    case FREQUENCY.HOURLY: return `Hourly at :${pad(toNumber(s.minute))}`;
    case FREQUENCY.DAILY: return `Daily at ${time}`;
    case FREQUENCY.WEEKLY: return `Weekly on ${DAYS[toNumber(s.dayOfWeek)] || 'Monday'} at ${time}`;
    case FREQUENCY.CUSTOM: return s.cronExpression ? `Custom · ${s.cronExpression}` : frequencyLabel(FREQUENCY.CUSTOM);
    default: return frequencyLabel(FREQUENCY.MANUAL);
  }
}

/**
 * Header meta + callout for the history dialog.
 * @param {object} state - { dataSource, runs, now }
 */
export function historyHeader({ dataSource, runs, now = new Date() }) {
  const ds = dataSource || {};
  const mapping = ds.fieldMapping || {};
  const identityColumn = typeof mapping.identityColumn === 'string' ? mapping.identityColumn.trim() : '';
  const identityName = identityColumn ? ((mapping.fieldMappings && mapping.fieldMappings[identityColumn]) || identityColumn) : '';
  const scheduled = isScheduled(ds.scheduleConfig && ds.scheduleConfig.frequency);
  const failing = scheduled && !identityColumn;
  const next = toDate(ds.nextExecutionAt);

  let identityCallout = null;
  if (!identityColumn) {
    const manual = (runs || []).filter((run) => run.triggerType === TRIGGER.MANUAL && run.status !== EXECUTION_STATUS.FAILED && run.status !== EXECUTION_STATUS.RUNNING);
    const added = manual.reduce((sum, run) => sum + toNumber(run.recordsImported), 0);
    const pulls = manual.length === 1 ? 'manual pull' : 'manual pulls';
    identityCallout = {
      title: 'No identity column — nothing is ever updated.',
      text: `${manual.length ? `The last ${manual.length} ${pulls} shown added ${fmtCount(added)} rows between them, including copies of the same records. ` : 'Every pull adds all rows again instead of updating them. '}${scheduled ? 'Scheduled runs fail until an identity column is mapped.' : 'Scheduled pulls need one.'}`,
    };
  }

  return {
    schedule: `${scheduleLabel(ds.scheduleConfig)}${failing ? ' — failing' : ''}`,
    nextRun: next && scheduled && !failing ? `${dayLabel(next, now)} ${timeLabel(next)}` : '',
    identity: identityName,
    identityCallout,
  };
}
